import { FC } from 'react';
import { Box, Typography } from '@mui/material';

import styles from './SmartTools.module.scss';
import { getSmartTools } from '../../config/activityFieldsConfig';
import {
  SmartToolsAlternateFlightsIcon,
  SmartToolsFlightDelayPredictionIcon,
  SmartToolsGoNowIcon,
  SmartToolsNavigatorIcon,
  SmartToolsPlacesNearbyIcon,
} from '../../../../../../../components/Icons';
import { SmartToolsPackingListIcon } from '../../../../../../../components/Icons/SmartToolsPackingListIcon';

interface SmartToolsProps {
  activityType: string;
  showGoNow?: boolean;
  onFlightDelayPrediction?: () => void;
  onGoNow?: () => void;
  onPackingList?: () => void;
  onAlternativeFlights?: () => void;
}

const SmartTools: FC<SmartToolsProps> = ({
  activityType,
  showGoNow = true,
  onFlightDelayPrediction,
  onGoNow,
  onPackingList,
  onAlternativeFlights,
}) => {
  const getFallbackTools = () => {
    const fallbackTools: string[] = [];

    if (showGoNow) {
      fallbackTools.push('Go Now');
    }

    fallbackTools.push('Packing List');

    return fallbackTools;
  };

  let tools: string[] = [];

  try {
    tools = getSmartTools(activityType);
  } catch (error) {
    console.error('Error getting smart tools:', error);
    tools = getFallbackTools();
  }

  if (!Array.isArray(tools) || tools.length === 0) {
    console.warn('Tools array is empty, using emergency fallback');
    tools = getFallbackTools();
  }

  const getToolIcon = (toolName: string) => {
    try {
      switch (toolName) {
        case 'Navigator':
          return <SmartToolsNavigatorIcon size={24} />;
        case 'Places Nearby':
          return <SmartToolsPlacesNearbyIcon size={24} />;
        case 'Go Now':
          return <SmartToolsGoNowIcon size={24} />;
        case 'Flight Delay Prediction':
          return <SmartToolsFlightDelayPredictionIcon size={24} />;
        case 'Alternative Flights':
          return <SmartToolsAlternateFlightsIcon size={24} />;
        case 'Packing List':
          return <SmartToolsPackingListIcon size={24} />;
        default:
          return <SmartToolsNavigatorIcon size={24} />;
      }
    } catch (error) {
      console.warn(`Error getting icon for tool ${toolName}:`, error);
      return <SmartToolsNavigatorIcon size={24} />;
    }
  };

  const handleToolClick = (toolName: string) => {
    try {
      switch (toolName) {
        case 'Go Now':
          if (onGoNow) {
            onGoNow();
          } else {
            console.log('Go Now: callback not provided');
          }
          break;

        case 'Packing List':
          if (onPackingList) {
            onPackingList();
          } else {
            console.log('Packing List: callback not provided');
          }
          break;
        case 'Alternative Flights':
          if (onAlternativeFlights) {
            onAlternativeFlights();
          } else {
            console.log('Alternative Flights: callback not provided');
          }
          break;
        case 'Flight Delay Prediction':
          if (onFlightDelayPrediction) {
            onFlightDelayPrediction();
          } else {
            console.log('Flight Delay Prediction: callback not provided');
          }
          break;
        default:
          console.log(`Smart tool clicked: ${toolName} for activity type: ${activityType}`);
          break;
      }
    } catch (error) {
      console.error(`Error handling tool click for ${toolName}:`, error);
      console.log(`Tool clicked: ${toolName} (error occurred)`);
    }
  };

  const validTools = tools.filter(tool => {
    return tool && typeof tool === 'string' && tool.trim() !== '';
  });

  let finalTools = validTools.length > 0 ? validTools : getFallbackTools();
  if (!showGoNow) {
    finalTools = finalTools.filter(tool => tool !== 'Go Now');
  }
  if (finalTools.length === 0) {
    finalTools = getFallbackTools();
  }

  return (
    <Box className={styles.smartTools}>
      <Typography
        variant="h10Bold"
        className={styles.sectionTitle}
        sx={{ color: '#222B3A' }}
      >
        Smart Tools
      </Typography>

      <Box className={styles.toolsContainer}>
        {finalTools.map(tool => (
          <button
            key={tool}
            className={styles.toolButton}
            onClick={() => handleToolClick(tool)}
          >
            {getToolIcon(tool)}
            <Typography
              variant="h10"
              className={styles.toolText}
              sx={{ color: '#222B3A' }}
            >
              {tool}
            </Typography>
          </button>
        ))}
      </Box>
    </Box>
  );
};

export default SmartTools;
